import { Formik, useField } from 'formik';
import * as Yup from 'yup';

import { submitChangePassword } from '../../store/profileSlice';
import type { ChangePasswordRequest } from '../../types/profile.types';

import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import { CancelButton } from '@components/Form';
import { SubmitButton } from '@components/Form/SubmitButton';
import { useNotification } from '@hooks/useNotification';
import { useAppDispatch } from '@store/hooks';

interface Props {
  onCancel: () => void;
}

interface PasswordInputProps {
  label: string;
  name: string;
  autoComplete?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  name,
  autoComplete,
}) => {
  const [field, meta] = useField(name);
  return (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <input
        {...field}
        type="password"
        autoComplete={autoComplete}
        className={`block w-full shadow-sm lg:text-sm md:text-xs text-xs rounded-md border ${
          meta.touched && meta.error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-primary-300 focus:border-primary-500 focus:ring-primary-500'
        }`}
      />
      {meta.touched && meta.error && (
        <p className="text-xs text-red-500">{meta.error}</p>
      )}
    </div>
  );
};

const validationSchema = Yup.object({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm your new password')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match'),
});

const ChangePasswordForm: React.FC<Props> = ({ onCancel }) => {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useNotification();

  return (
    <Box size="medium">
      <Formik
        initialValues={{
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }}
        validationSchema={validationSchema}
        validateOnBlur
        validateOnChange
        onSubmit={(values, actions) => {
          const request: ChangePasswordRequest = {
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
          };

          dispatch(submitChangePassword(request))
            .unwrap()
            .then(() => {
              showSuccess('Password changed successfully.');
              onCancel();
            })
            .catch((err: string) => {
              showError(err);
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        }}
      >
        {(formikProps) => (
          <form onSubmit={formikProps.handleSubmit}>
            <FormSectionContainer>
              <PasswordInput
                label="Current Password"
                name="currentPassword"
                autoComplete="current-password"
              />
              <PasswordInput
                label="New Password"
                name="newPassword"
                autoComplete="new-password"
              />
              <PasswordInput
                label="Confirm New Password"
                name="confirmPassword"
                autoComplete="new-password"
              />
            </FormSectionContainer>
            <FormButtonsContainer>
              <CancelButton onClick={onCancel} />
              <SubmitButton label="Save" disabled={formikProps.isSubmitting} />
            </FormButtonsContainer>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ChangePasswordForm;
